import { generateTrackingId } from "client/Misc/utils";
import { never } from "client_legacy/ndaw/errors";
import {
	ReactElement,
	RefObject,
	useMemo,
	useRef
} from "react";
import { ContentLayout } from "./ContentLayout/ContentLayout";
import { getHubImageUrl, HubItemElementKind } from "client/Api/Hub/common";
import { ImageAttributionViewModel } from "client/Api/common";
import {
	HubItemColumnDetailsViewModel,
	HubItemElementDetailsViewModel,
	HubItemLayoutDetailsViewModel,
	HubItemRowDetailsViewModel,
	ImageElementDetailsViewModel
} from "client/Api/Hub/common.details";
import { useResizeObserver } from "client/Hooks";
import { ROW_BREAKPOINT_WIDTH } from "../Layout/Layout";
import { HubItemKind } from "client/Api/Hub/hubItemCategories";
import { FaqContentViewerEmptyBlockSettings } from "./Elements/FrequentlyAskedQuestions/FrequentlyAskedQuestions";
import { hubItemKindToHubDataSourceMap } from "../../Sidebar/common";
import { ImageUrlWithAttribution } from "../../common";

const {
	Row,
	Column,
	HorizontalLine,
	Image,
	Paragraph,
	Subtitle,
	Video,
	Embed,
	FrequentlyAskedQuestions
} = ContentLayout;

export type ContentViewerEmptyBlockBehavior =
| {
	kind: "empty";
}
| {
	kind: "placeholder";
	placeholder: string;
};

export type ContentViewerEmptyBlockSettings = {
	faqElement?: FaqContentViewerEmptyBlockSettings;
};

export type ContentViewerProps = {
	hubItemKind: HubItemKind;
	layout: HubItemLayoutContentViewerModel;
	onViewImage?: (image: ImageUrlWithAttribution) => void;
	emptyBlockSettings?: ContentViewerEmptyBlockSettings;
};

export const ContentViewer = (props: ContentViewerProps): ReactElement => {
	const {
		hubItemKind,
		layout,
		onViewImage,
		emptyBlockSettings
	} = props;

	const layoutRootRef: RefObject<HTMLDivElement | null> = useRef<HTMLDivElement>(null);
	const { width } = useResizeObserver(layoutRootRef);

	const hasStackedColumns: boolean = useMemo(
		(): boolean => width < ROW_BREAKPOINT_WIDTH,
		[width]
	);

	const renderElement = (
		element: HubItemElementContentViewerModel
	): ReactElement => {

		switch (element.kind) {
			case HubItemElementKind.Paragraph:
				return (
					<Paragraph
						key={element.id}
						html={element.html ?? ""}
					/>
				);
			case HubItemElementKind.Subtitle:
				return (
					<Subtitle
						key={element.id}
						text={element.text}
					/>
				);
			case HubItemElementKind.HorizontalLine:
				return (
					<HorizontalLine
						key={element.id}
					/>
				);
			case HubItemElementKind.Image:
				let imageViewerItem: ImageUrlWithAttribution;

				if (isInlineImageElement(element)) {
					imageViewerItem = {
						url: element.uri,
						attribution: element.attribution
					};
				} else if (isBlobIdImageElement(element)) {
					imageViewerItem = {
						url: getHubImageUrl(
							hubItemKindToHubDataSourceMap[hubItemKind],
							element.blobId
						),
						attribution: element.attribution
					};
				} else {
					return <div/>;
				}

				return (
					<Image
						key={element.id}
						name={element.name}
						trackingId={generateTrackingId("image", `|${element.id}`)}
						src={imageViewerItem.url}
						onClick={() => onViewImage?.(imageViewerItem)}
					/>
				);
			case HubItemElementKind.Video:
				return (
					<Video
						key={element.id}
						url={element.url}
					/>
				);
			case HubItemElementKind.Embed:
				return (
					<Embed
						key={element.id}
						code={element.code}
					/>
				);
			case HubItemElementKind.FrequentlyAskedQuestion:
				return (
					<FrequentlyAskedQuestions
						key={element.id}
						items={element.items}
						emptySettings={emptyBlockSettings?.faqElement}
					/>
				);
			default:
				never(element);
		}
	};

	return (
		<div ref={layoutRootRef}>
			<ContentLayout>
				{layout.rows.map(
					(row, index) => (
						<Row
							key={index}
							rowKind={row.layout}
							trackingId={generateTrackingId("row", `|${index}`)}
							hasStackedColumns={hasStackedColumns}
						>
							{row.columns.map(
								(column, index) => (
									<Column
										key={index}
										trackingId={generateTrackingId("column", `|${index}`)}
									>
										{column.elements
											.map(renderElement)}
									</Column>
								)
							)}
						</Row>
					)
				)}
			</ContentLayout>
		</div>

	);
};
ContentViewer.displayName = "ContentViewer";

export type HubItemLayoutContentViewerModel = OverrideProperties<
	HubItemLayoutDetailsViewModel,
	{
		rows: readonly HubItemRowContentViewerModel[];
	}
>;

export type HubItemRowContentViewerModel = OverrideProperties<
	HubItemRowDetailsViewModel,
	{
		columns: readonly HubItemColumnContentViewerModel[];
	}
>;

export type HubItemColumnContentViewerModel = OverrideProperties<
	HubItemColumnDetailsViewModel,
	{
		elements: readonly HubItemElementContentViewerModel[];
	}
>;

export type HubItemElementContentViewerModel =
	| HubItemElementDetailsViewModel
	| InlineImageElementContentViewerModel
	| NotSetImageElementContentViewerModel;

export type InlineImageElementContentViewerModel = {
	id: number;
	kind: HubItemElementKind.Image;
	name: string;
	uri: string;
	attribution: ImageAttributionViewModel | null;
};

export type NotSetImageElementContentViewerModel = {
	id: number;
	kind: HubItemElementKind.Image;
	value: "NONE";
};

function isInlineImageElement(
	element: ImageElementDetailsViewModel
	| InlineImageElementContentViewerModel
	| NotSetImageElementContentViewerModel
): element is InlineImageElementContentViewerModel {
	return typeof (element as InlineImageElementContentViewerModel).uri === "string";
}

function isBlobIdImageElement(
	element: ImageElementDetailsViewModel
	| InlineImageElementContentViewerModel
	| NotSetImageElementContentViewerModel
): element is ImageElementDetailsViewModel {
	return typeof(element as ImageElementDetailsViewModel).blobId === "string";
}